import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { FormField } from '../../../../global/form/form-field/form-field';
import { FormFieldError } from '../../../../global/form/form-field-input/form-field-error';
import { SingleSelectList } from '../../../../global/single-select-list/single-select-list';
import { ChevronDownIcon } from '../../../../global/icons/icons';
import { REQUIRED_FIELD_TEXT } from '../../../../../constants/common-labels.constants';
import { FormFieldProps } from '../../../../../infrastructure/form-viewmodel-controller';
import { debounce } from '../../../../../infrastructure/utils/debounce.utils';
import { Tooltip } from '../../../../global/tooltip/tooltip';
import {
    LOOKUP_ERROR_TEXT,
    LOOKUP_LOADING_TEXT,
    LOOKUP_MIN_CHARS_HINT_TEXT,
    LOOKUP_NO_RESULTS_TEXT,
    LOOKUP_CLEAR_OPTION_TEXT,
    LOOKUP_PLACEHOLDER_TEXT,
    LOOKUP_SEARCH_PLACEHOLDER_TEXT,
} from './salesforce-order.texts';
import './lookup-select.scss';

const SEARCH_DEBOUNCE_MS = 350;
const MAX_VISIBLE_OPTIONS = 50;

const LOOKUP_COLUMN_KEY = {
    eventName: 'eventName',
    lookupTitle: 'lookupTitle',
    requesterName: 'requesterName',
    eventType: 'eventType',
    orderNumber: 'orderNumber',
    dates: 'dates',
} as const;

interface ILookupColumn {
    key: string;
    value: string;
}

type LookupColumn = string | ILookupColumn;

interface ILookupOption {
    value: string;
    label: string;
    columns?: LookupColumn[];
}

interface IProps {
    field: FormFieldProps;
    labelDescriptor: MessageDescriptor;
    required?: boolean;
    allowClear?: boolean;
    disabled?: boolean;
    onChange?: (value: string, option?: ILookupOption) => void;
    options?: ILookupOption[];
    onSearch?: (term: string, signal: AbortSignal) => Promise<ILookupOption[]>;
    minChars?: number;
    initialResults?: () => { term: string; options: ILookupOption[] } | null;
    initialSelected?: ILookupOption | null;
    autoOpen?: boolean;
    autoOpenReady?: boolean;
    menuClassName?: string;
    selectedLabelKey?: string;
    refineStrategy?: 'search-each-change' | 'local-then-fallback' | 'filter-original';
    keyboardNavigation?: boolean;
    columnTooltips?: boolean;
}

const includesCI = (haystack: string | null | undefined, needle: string | null | undefined): boolean =>
    String(haystack ?? '').toUpperCase().indexOf(String(needle ?? '').toUpperCase()) !== -1;

const columnText = (column: LookupColumn): string => {
    const value = typeof column === 'string' ? column : column?.value;
    return value ?? '';
};

const columnKey = (column: LookupColumn): string | undefined =>
    typeof column === 'string' ? undefined : column.key;

const selectedDisplayLabel = (option: ILookupOption | null | undefined, selectedLabelKey?: string): string | undefined => {
    if (!option) {
        return undefined;
    }
    if (selectedLabelKey) {
        const column = (option.columns ?? []).find(item => columnKey(item) === selectedLabelKey);
        const text = column ? columnText(column).trim() : '';
        if (text) {
            return text;
        }
    }
    return option.label;
};

const isEndAlignedColumn = (column: LookupColumn, index: number): boolean => {
    const key = columnKey(column);
    if (key) {
        return key === LOOKUP_COLUMN_KEY.orderNumber || key === LOOKUP_COLUMN_KEY.dates;
    }
    return index === 1 || index === 2;
};

const isTitleColumn = (column: LookupColumn): boolean => {
    const key = columnKey(column);
    return key === LOOKUP_COLUMN_KEY.eventName || key === LOOKUP_COLUMN_KEY.lookupTitle;
};

const isStackedOption = (option: ILookupOption): boolean =>
    (option.columns ?? []).some(isTitleColumn);

const optionMatchesFilter = (option: ILookupOption, filter: string): boolean => {
    if (!filter) {
        return true;
    }
    if (includesCI(option.label, filter)) {
        return true;
    }
    return (option.columns ?? []).some(column => includesCI(columnText(column), filter));
};

const resolveSelectedOption = (
    selectedValue: string,
    pools: (() => ILookupOption[] | undefined)[],
): ILookupOption | undefined => {
    if (!selectedValue) {
        return undefined;
    }
    let match: ILookupOption | undefined;
    pools.find(getPool => {
        match = getPool()?.find(option => option.value === selectedValue);
        return !!match;
    });
    return match;
};

const withSelectedFirst = (
    visible: ILookupOption[],
    selectedOption: ILookupOption | undefined,
    filter: string,
): ILookupOption[] => {
    if (!selectedOption) {
        return visible;
    }
    const selectedIsVisible = visible.some(option => option.value === selectedOption.value);
    if (filter && !selectedIsVisible) {
        return visible;
    }
    return [selectedOption, ...visible.filter(option => option.value !== selectedOption.value)];
};

const ColumnCell = (props: {
    column: LookupColumn;
    index: number;
    extraClass?: string;
    showTooltip?: boolean;
}): JSX.Element => {
    const textValue = columnText(props.column);
    const text = <span className='lookup-select__col-text'>{textValue}</span>;
    const showTooltip = props.showTooltip !== false && !!textValue;

    return (
        <span
            className={classnames('lookup-select__col', props.extraClass, {
                'lookup-select__col--end': isEndAlignedColumn(props.column, props.index),
            })}
        >
            {showTooltip ? (
                <Tooltip content={textValue} className='lookup-select__col-tooltip'>
                    {text}
                </Tooltip>
            ) : text}
        </span>
    );
};

const LookupSelect = observer((props: IProps) => {
    const {
        field, labelDescriptor, options, required, allowClear, disabled, onChange, onSearch, minChars,
        initialResults, initialSelected, autoOpen, autoOpenReady, menuClassName, selectedLabelKey,
        refineStrategy = 'search-each-change', keyboardNavigation = false, columnTooltips = true,
    } = props;
    const intl = useIntl();
    const isAsync = !!onSearch;
    const effectiveMinChars = minChars ?? (isAsync ? 3 : 0);

    const [isOpen, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    const [results, setResults] = useState<ILookupOption[]>([]);
    const [searchedTerm, setSearchedTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [asyncSelected, setAsyncSelected] = useState<ILookupOption | null>(initialSelected ?? null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [highlightMoved, setHighlightMoved] = useState(false);
    const requestIdRef = useRef(0);
    const searchedTermsRef = useRef<Set<string>>(new Set());
    const requestedTermRef = useRef('');
    const filterRef = useRef('');
    const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchAbortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => () => {
        mountedRef.current = false;
        searchAbortRef.current?.abort();
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
        }
    }, []);

    const resetHighlight = (): void => {
        setHighlightMoved(false);
        setActiveIndex(0);
    };

    const runSearchRef = useRef<(term: string) => void>(() => undefined);
    runSearchRef.current = (term: string): void => {
        if (!onSearch || !mountedRef.current) {
            return;
        }
        if (refineStrategy === 'local-then-fallback' && filterRef.current.toUpperCase() !== term.toUpperCase()) {
            return;
        }
        if (refineStrategy === 'filter-original') {
            requestedTermRef.current = term;
        }
        searchAbortRef.current?.abort();
        const controller = new AbortController();
        searchAbortRef.current = controller;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(false);
        onSearch(term, controller.signal)
            .then(res => {
                if (!mountedRef.current || requestId !== requestIdRef.current) {
                    return;
                }
                if (refineStrategy === 'local-then-fallback') {
                    searchedTermsRef.current = new Set([term.toUpperCase()]);
                }
                setResults(res);
                setSearchedTerm(term);
                setLoading(false);
                resetHighlight();
            })
            .catch(() => {
                if (mountedRef.current && requestId === requestIdRef.current) {
                    if (refineStrategy === 'filter-original') {
                        requestedTermRef.current = '';
                    }
                    setError(true);
                    setLoading(false);
                }
            });
    };
    const debouncedSearch = useRef(debounce((term: string) => runSearchRef.current(term), SEARCH_DEBOUNCE_MS)).current;

    const close = (): void => {
        setOpen(false);
        setFilter('');
        filterRef.current = '';
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
        resetHighlight();
    };

    const openMenu = (): void => {
        if (isAsync && results.length === 0 && initialResults) {
            const cached = initialResults();
            if (cached) {
                setResults(cached.options);
                setSearchedTerm(cached.term);
                if (cached.term) {
                    searchedTermsRef.current.add(cached.term.toUpperCase());
                }
            }
        }
        resetHighlight();
        setOpen(true);
    };

    const toggleMenu = (): void => {
        if (disabled) {
            return;
        }
        isOpen ? close() : openMenu();
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleDocClick = (event: MouseEvent): void => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                close();
            }
        };
        document.addEventListener('mousedown', handleDocClick);
        return () => document.removeEventListener('mousedown', handleDocClick);
    }, [isOpen]);

    useEffect(() => {
        if (!isAsync) {
            return;
        }
        const currentValue = (field.value as string) || '';
        if (!currentValue) {
            if (asyncSelected !== null) {
                setAsyncSelected(null);
            }
            return;
        }
        if (asyncSelected?.value === currentValue) {
            return;
        }
        const cached = initialResults ? initialResults() : null;
        const match = results.find(option => option.value === currentValue)
            ?? cached?.options.find(option => option.value === currentValue)
            ?? (initialSelected?.value === currentValue ? initialSelected : undefined)
            ?? { value: currentValue, label: currentValue };
        setAsyncSelected(match);
    }, [isAsync, field.value, results, initialResults, asyncSelected, initialSelected]);

    const autoOpenedRef = useRef(false);
    useEffect(() => {
        if (!autoOpen || autoOpenedRef.current || disabled) {
            return;
        }
        if (isAsync) {
            if (!autoOpenReady || !initialResults) {
                return;
            }
            const cached = initialResults();
            if (cached && cached.options.length > 0) {
                autoOpenedRef.current = true;
                setResults(cached.options);
                setSearchedTerm(cached.term);
                if (cached.term) {
                    searchedTermsRef.current.add(cached.term.toUpperCase());
                }
                setOpen(true);
            }
            return;
        }
        if ((options ?? []).length > 0) {
            autoOpenedRef.current = true;
            setOpen(true);
        }
    }, [autoOpen, disabled, isAsync, options?.length, autoOpenReady, initialResults]);

    const cancelFallbackSearch = (): void => {
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
    };

    const scheduleFallbackSearch = (term: string): void => {
        cancelFallbackSearch();
        fallbackTimerRef.current = setTimeout(() => {
            fallbackTimerRef.current = null;
            runSearchRef.current(term);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleFilterChange = (value: string): void => {
        setFilter(value);
        filterRef.current = value;
        resetHighlight();
        if (!isAsync || value.length < effectiveMinChars) {
            if (refineStrategy === 'local-then-fallback' || refineStrategy === 'filter-original') {
                cancelFallbackSearch();
            }
            if (refineStrategy === 'filter-original') {
                requestedTermRef.current = '';
            }
            return;
        }
        if (refineStrategy === 'filter-original') {
            const requested = requestedTermRef.current;
            const keepsOriginalResults = requested.length > 0
                && value.toUpperCase().startsWith(requested.toUpperCase());
            if (keepsOriginalResults) {
                cancelFallbackSearch();
                return;
            }
            scheduleFallbackSearch(value);
            return;
        }
        if (refineStrategy !== 'local-then-fallback') {
            debouncedSearch(value);
            return;
        }
        const extendsLastSearch = searchedTerm.length > 0
            && value.length > searchedTerm.length
            && value.toUpperCase().startsWith(searchedTerm.toUpperCase());
        const hasLocalMatch = extendsLastSearch && results.some(option => optionMatchesFilter(option, value));
        const skipSearch = searchedTermsRef.current.has(value.toUpperCase()) || hasLocalMatch;
        if (skipSearch) {
            cancelFallbackSearch();
            return;
        }
        scheduleFallbackSearch(value);
    };

    const handleSelect = (value: string): void => {
        close();
        const option = results.find(item => item.value === value)
            ?? (options ?? []).find(item => item.value === value)
            ?? (asyncSelected?.value === value ? asyncSelected : undefined)
            ?? initialResults?.()?.options.find(item => item.value === value)
            ?? (initialSelected?.value === value ? initialSelected : undefined)
            ?? (value ? { value: value, label: value } : { value: '', label: '' });
        if (onChange) {
            onChange(value, option);
        } else {
            field.setValue(value);
        }
        field.setTouched();
        if (isAsync) {
            setAsyncSelected(value ? option : null);
        }
    };

    const showMinCharsHint = isAsync && results.length === 0 && filter.length < effectiveMinChars;

    const selectedLabel = useMemo(() => {
        if (!isAsync) {
            return selectedDisplayLabel((options ?? []).find(option => option.value === field.value), selectedLabelKey);
        }
        const currentValue = (field.value as string) || '';
        if (!currentValue) {
            return undefined;
        }
        if (asyncSelected?.value === currentValue) {
            return selectedDisplayLabel(asyncSelected, selectedLabelKey);
        }
        if (initialSelected?.value === currentValue) {
            return selectedDisplayLabel(initialSelected, selectedLabelKey);
        }
        return undefined;
    }, [isAsync, asyncSelected, options, field.value, initialSelected, selectedLabelKey]);

    const displayed = useMemo(
        () => {
            if (!isAsync) {
                return filter ? (options ?? []).filter(option => optionMatchesFilter(option, filter)) : (options ?? []);
            }
            // The server (or its exact-term cache) already matched this term. Re-filtering
            // would drop requester-only hits once that column is blanked in stored results.
            if (searchedTerm.length > 0 && filter.toUpperCase() === searchedTerm.toUpperCase()) {
                return results;
            }
            return results.filter(option => optionMatchesFilter(option, filter));
        },
        [isAsync, results, filter, options, searchedTerm],
    );

    const visible = displayed.slice(0, MAX_VISIBLE_OPTIONS);
    const showClearInMenu = allowClear || !required;
    const showingResultRows = !loading && !error && !showMinCharsHint
        && (displayed.length > 0 || showClearInMenu);
    const selectedValue = (field.value as string) || '';
    const selectedOption = resolveSelectedOption(selectedValue, [
        () => visible,
        () => results,
        () => options,
        () => (isOpen ? initialResults?.()?.options : undefined),
        () => (asyncSelected ? [asyncSelected] : undefined),
    ]);
    const orderedOptions = withSelectedFirst(visible, selectedOption, filter);
    const menuItems: ILookupOption[] = showClearInMenu
        ? [{ value: '', label: '' }, ...orderedOptions]
        : orderedOptions;
    const highlightedValue = menuItems[Math.min(activeIndex, Math.max(menuItems.length - 1, 0))]?.value;

    useEffect(() => {
        if (!keyboardNavigation || !isOpen) {
            return;
        }
        const row = rootRef.current?.querySelector('.lookup-select__row--active');
        if (row && typeof row.scrollIntoView === 'function') {
            row.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex, keyboardNavigation, isOpen, highlightedValue]);

    const renderMenuBody = (): React.ReactNode => {
        const showClearOption = allowClear || !required;
        const noneRow = (
            <div
                className='lookup-select__row lookup-select__row--none'
                role='button'
                tabIndex={0}
                onClick={() => handleSelect('')}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelect('');
                    }
                }}
            >
                <FormattedMessage {...LOOKUP_CLEAR_OPTION_TEXT} />
            </div>
        );

        if (showMinCharsHint) {
            const hint = (
                <div className='lookup-select__hint'>
                    <FormattedMessage {...LOOKUP_MIN_CHARS_HINT_TEXT} values={{ min: effectiveMinChars }} />
                </div>
            );
            if (showClearOption && !filter) {
                return <div className='lookup-select__list'>{noneRow}{hint}</div>;
            }
            return hint;
        }
        if (loading) {
            return <div className='lookup-select__hint'><FormattedMessage {...LOOKUP_LOADING_TEXT} /></div>;
        }
        if (error) {
            return <div className='lookup-select__error'><FormattedMessage {...LOOKUP_ERROR_TEXT} /></div>;
        }
        if (displayed.length === 0) {
            if (showClearOption) {
                return <div className='lookup-select__list'>{noneRow}</div>;
            }
            return <div className='lookup-select__no-results'><FormattedMessage {...LOOKUP_NO_RESULTS_TEXT} /></div>;
        }

        const listItems = showClearOption
            ? [{ value: '', label: intl.formatMessage(LOOKUP_CLEAR_OPTION_TEXT) }, ...orderedOptions]
            : orderedOptions;

        if (listItems.some(option => option.columns)) {
            return (
                <div className='lookup-select__list'>
                    {listItems.map(option => {
                        const columns = option.columns ?? [option.label];
                        const isStacked = isStackedOption(option);
                        const titleColumn = (option.columns ?? []).find(isTitleColumn);
                        const title = titleColumn ? columnText(titleColumn) : columnText(columns[0]);
                        const meta = isStacked
                            ? (option.columns ?? []).filter(column => column !== titleColumn)
                            : [];

                        return (
                            <div
                                key={option.value || 'none'}
                                className={classnames('lookup-select__row', {
                                    'lookup-select__row--selected': option.value === selectedValue,
                                    'lookup-select__row--none': !option.value,
                                    'lookup-select__row--stacked': isStacked,
                                    'lookup-select__row--active': keyboardNavigation
                                        && highlightMoved
                                        && option.value === highlightedValue,
                                })}
                                role='button'
                                tabIndex={0}
                                onClick={() => handleSelect(option.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleSelect(option.value);
                                    }
                                }}
                            >
                                {isStacked ? (
                                    <>
                                        <ColumnCell
                                            column={title}
                                            index={0}
                                            extraClass='lookup-select__title'
                                            showTooltip={columnTooltips}
                                        />
                                        <span className='lookup-select__meta'>
                                            {meta.map((column, index) => (
                                                <ColumnCell
                                                    key={columnKey(column) ?? index + 1}
                                                    column={column}
                                                    index={index + 1}
                                                    showTooltip={columnTooltips}
                                                />
                                            ))}
                                        </span>
                                    </>
                                ) : (
                                    columns.map((column, index) => (
                                        <ColumnCell
                                            key={columnKey(column) ?? index}
                                            column={column}
                                            index={index}
                                            showTooltip={columnTooltips}
                                        />
                                    ))
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }

        return (
            <div className='lookup-select__list'>
                <SingleSelectList
                    items={listItems}
                    value={(field.value as string) || ''}
                    onChange={handleSelect}
                    keyProperty='value'
                    nameProperty='label'
                />
            </div>
        );
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (!keyboardNavigation || menuItems.length === 0) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightMoved(true);
            setActiveIndex(index => Math.min(index + 1, menuItems.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightMoved(true);
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (!showingResultRows || !highlightMoved) {
                return;
            }
            const item = menuItems[Math.min(activeIndex, menuItems.length - 1)];
            if (item) {
                handleSelect(item.value);
            }
        }
    };

    return (
        <FormField
            className='m-bottom-20px'
            label={<>
                <FormattedMessage {...labelDescriptor} />
                {required && <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>}
            </>}
        >
            <div className='lookup-select' ref={rootRef}>
                <div
                    className={classnames('lookup-select__control', {
                        'lookup-select__control--open': isOpen,
                        'lookup-select__control--placeholder': !selectedLabel,
                        'lookup-select__control--disabled': disabled,
                    })}
                    role='button'
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onClick={toggleMenu}
                    onKeyDown={event => {
                        if (disabled) {
                            return;
                        }
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleMenu();
                        } else if (event.key === 'Escape') {
                            close();
                        }
                    }}
                >
                    <span className='lookup-select__value'>
                        {selectedLabel || intl.formatMessage(LOOKUP_PLACEHOLDER_TEXT)}
                    </span>
                    <ChevronDownIcon />
                </div>

                {isOpen && (
                    <div className={classnames('lookup-select__menu', menuClassName)}>
                        <input
                            type='text'
                            className='lookup-select__search'
                            autoFocus
                            placeholder={intl.formatMessage(LOOKUP_SEARCH_PLACEHOLDER_TEXT)}
                            value={filter}
                            onChange={event => handleFilterChange(event.target.value)}
                            onKeyDown={handleSearchKeyDown}
                        />
                        {renderMenuBody()}
                    </div>
                )}
            </div>
            <FormFieldError field={field} labelDescriptor={labelDescriptor} />
        </FormField>
    );
});

export { LookupSelect, ILookupOption, ILookupColumn, LOOKUP_COLUMN_KEY };
